/**
 * Snapping math — grid snap, object edge/center snap, and the smart-guide
 * lines that visualize an engaged snap.
 *
 * Runs DURING a drag (per gesture event) against the pre-culled candidate
 * set, so everything here is allocation-light and branch-simple. The
 * tolerance is passed in WORLD units — callers convert the screen-px
 * constant (`SNAP_DISTANCE_PX / zoom`) so the magnet feels constant on
 * screen at every zoom level.
 *
 * Precedence contract (resolveSnap): OBJECT snap beats GRID snap per axis —
 * aligning to a neighbor is a stronger intent signal than the grid; the
 * grid catches the axis when no neighbor is in range.
 *
 * @module floorplan/snapping/snap
 * @category FloorPlan
 */

import type { Bounds } from '../models/geometry';

/** A rendered alignment line (world coords) for the guide plane. */
export type SnapGuide = {
  /** 'x' = vertical line at `position`; 'y' = horizontal line. */
  axis: 'x' | 'y';
  position: number;
  /** Segment extent along the other axis (spans both snapped bounds). */
  from: number;
  to: number;
};

export type AxisSnap = {
  /** Correction to add to the moving bounds on this axis. */
  delta: number;
  guide: SnapGuide;
};

export type SnapResult = {
  dx: number;
  dy: number;
  guides: SnapGuide[];
};

export const NO_SNAP: SnapResult = { dx: 0, dy: 0, guides: [] };

/** Round a scalar to the nearest grid line. */
export const snapValueToGrid = (value: number, gridSize: number): number => {
  'worklet';

  if (gridSize <= 0) return value;
  const snapped = Math.round(value / gridSize) * gridSize;
  // Normalize -0 (Math.round(-0.4) === -0) so it never leaks into documents.
  if (snapped === 0) return 0;
  return snapped;
};

/** Grid-snap a bounds' top-left corner. Returns the per-axis correction. */
export const snapBoundsToGrid = (
  bounds: Bounds,
  gridSize: number,
): { dx: number; dy: number } => {
  'worklet';

  return {
    dx: snapValueToGrid(bounds.x, gridSize) - bounds.x,
    dy: snapValueToGrid(bounds.y, gridSize) - bounds.y,
  };
};

/** The three snap stations of a bounds on one axis: start, center, end. */
const stationsX = (bounds: Bounds): [number, number, number] => {
  'worklet';

  return [bounds.x, bounds.x + bounds.width / 2, bounds.x + bounds.width];
};

const stationsY = (bounds: Bounds): [number, number, number] => {
  'worklet';

  return [bounds.y, bounds.y + bounds.height / 2, bounds.y + bounds.height];
};

/**
 * Best object snap on one axis: compare the moving bounds' three stations
 * against every candidate's three stations; the smallest |delta| within
 * tolerance wins. Returns null when nothing is in range.
 */
const bestAxisSnap = (
  axis: 'x' | 'y',
  moving: Bounds,
  candidates: readonly Bounds[],
  tolerance: number,
): AxisSnap | null => {
  'worklet';

  const movingStations = axis === 'x' ? stationsX(moving) : stationsY(moving);
  let best: AxisSnap | null = null;
  for (const candidate of candidates) {
    const candidateStations =
      axis === 'x' ? stationsX(candidate) : stationsY(candidate);
    for (const movingStation of movingStations) {
      for (const candidateStation of candidateStations) {
        const delta = candidateStation - movingStation;
        const distance = Math.abs(delta);
        if (distance > tolerance) continue;
        if (best !== null && distance >= Math.abs(best.delta)) continue;
        // Guide spans both rects on the perpendicular axis.
        const from =
          axis === 'x'
            ? Math.min(moving.y, candidate.y)
            : Math.min(moving.x, candidate.x);
        const to =
          axis === 'x'
            ? Math.max(moving.y + moving.height, candidate.y + candidate.height)
            : Math.max(moving.x + moving.width, candidate.x + candidate.width);
        best = {
          delta,
          guide: { axis, position: candidateStation, from, to },
        };
      }
    }
  }
  return best;
};

/** Object edge/center snapping on both axes. */
export const snapBoundsToObjects = (
  moving: Bounds,
  candidates: readonly Bounds[],
  tolerance: number,
): SnapResult => {
  'worklet';

  const snapX = bestAxisSnap('x', moving, candidates, tolerance);
  const snapY = bestAxisSnap('y', moving, candidates, tolerance);
  const guides: SnapGuide[] = [];
  if (snapX !== null) guides.push(snapX.guide);
  if (snapY !== null) guides.push(snapY.guide);
  return {
    dx: snapX === null ? 0 : snapX.delta,
    dy: snapY === null ? 0 : snapY.delta,
    guides,
  };
};

export type ResolveSnapOptions = {
  /** Static neighbor AABBs (the culled, non-dragged object set). */
  candidates: readonly Bounds[];
  /** World-unit magnet range (convert from `SNAP_DISTANCE_PX / zoom`). */
  tolerance: number;
  gridEnabled: boolean;
  gridSize: number;
  objectSnapEnabled: boolean;
};

/**
 * Combined snap resolution for a dragged bounds. Per axis: object snap
 * wins when in range; otherwise grid snap (when enabled). Guides only
 * accompany OBJECT snaps — the grid is its own visual.
 */
export const resolveSnap = (
  moving: Bounds,
  options: ResolveSnapOptions,
): SnapResult => {
  'worklet';

  const objectSnap = options.objectSnapEnabled
    ? snapBoundsToObjects(moving, options.candidates, options.tolerance)
    : NO_SNAP;
  let dx = objectSnap.dx;
  let dy = objectSnap.dy;
  const guides = [...objectSnap.guides];
  const snappedX = guides.some((guide) => guide.axis === 'x');
  const snappedY = guides.some((guide) => guide.axis === 'y');
  if (options.gridEnabled) {
    const grid = snapBoundsToGrid(moving, options.gridSize);
    if (!snappedX) dx = grid.dx;
    if (!snappedY) dy = grid.dy;
  }
  return { dx, dy, guides };
};
